import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, Bell, Copy, Edit3, Eraser, HelpCircle, Loader2, Microscope, RefreshCw, Save, ShieldAlert, Trash2, Wifi } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../services/firebaseService';
import { requestNotificationPermission } from '../../../services/notificationService';
import { auth, messaging, updateFcmToken, enableNotifications, dedupeUserCreatedSpots, type DedupeResult } from '../../../services/firebaseService';
import { getToken } from 'firebase/messaging';

type ExternalSyncRegion = {
  id: string;
  name: string;
  enabled: boolean;
  bounds: {
    latMin: number;
    lonMin: number;
    latMax: number;
    lonMax: number;
  };
};

type RegionSearchResult = {
  display_name: string;
  boundingbox?: string[];
  lat?: string;
  lon?: string;
};

type SeoReport = {
  month: string;
  totals: { views: number; humanViews: number; botViews: number };
  topPages: Array<{ page: string; views: number; humanViews: number; botViews: number }>;
  pageCount: number;
  sitemapUrl: string;
};

type AdminToolsPanelProps = {
  showToolsIntro: boolean;
  handleDismissToolsIntro: () => void;
  showSystemSettingsHelp: boolean;
  setShowSystemSettingsHelp: React.Dispatch<React.SetStateAction<boolean>>;
  showExternalSyncHelp: boolean;
  setShowExternalSyncHelp: React.Dispatch<React.SetStateAction<boolean>>;
  adminReevaluateButtonEnabled: boolean;
  handleToggleAdminReevaluateButton: (enabled: boolean) => Promise<void>;
  adminSubscriptionPushEnabled: boolean;
  handleToggleAdminSubscriptionPush: (enabled: boolean) => Promise<void>;
  activeTab: string;
  externalSyncEnabled: boolean;
  externalSyncSources: { nvdb: boolean; osmAmenities: boolean; osmCamp: boolean };
  setExternalSyncSources: React.Dispatch<React.SetStateAction<AdminToolsPanelProps['externalSyncSources']>>;
  handleSaveExternalSyncSources: () => Promise<void>;
  externalSyncStatus: any;
  externalSyncRegions: ExternalSyncRegion[];
  externalSyncRegionValidation: { isValid: boolean; issues: string[]; invalidIndexes: Set<number> };
  handleToggleExternalSync: (enabled: boolean) => Promise<void>;
  handleToggleExternalSyncSource: (source: keyof AdminToolsPanelProps['externalSyncSources']) => void;
  handleRunExternalSync: () => Promise<void>;
  handleUpdateExternalSyncRegion: (idx: number, field: 'id' | 'name' | 'enabled' | 'latMin' | 'lonMin' | 'latMax' | 'lonMax', value: any) => void;
  handleRemoveExternalSyncRegion: (idx: number) => void;
  handleAddExternalSyncRegion: () => void;
  handleSaveExternalSyncRegions: () => void;
  regionSearchQuery: string;
  setRegionSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  regionSearchLoading: boolean;
  regionSearchResults: RegionSearchResult[];
  handleApplyRegionSearchResult: (result: RegionSearchResult) => void;
  processingId: string | null;
  verifiedFeedUrl: string;
  handleCopyFeedUrl: () => Promise<void>;
  feedCopied: boolean;
  sitemapUrl: string;
  handleCopySitemapUrl: () => Promise<void>;
  sitemapCopied: boolean;
  loadSeoReport: () => Promise<void>;
  seoReportLoading: boolean;
  seoReport: SeoReport | null;
  confirmClearArea: boolean;
  setConfirmClearArea: React.Dispatch<React.SetStateAction<boolean>>;
  executeClearArea: () => Promise<void>;
  isClearingArea: boolean;
  executeReevaluation: () => Promise<void>;
  handleAddRegionFromCurrentView: () => void;
  nordicCountryConfig: Record<string, boolean>;
  handleToggleNordicCountry: (country: string, enabled: boolean) => Promise<void>;
};

const testAdminPush = httpsCallable(functions, 'testAdminPush');
const adminPublishAllWaypoints = httpsCallable(functions, 'adminPublishAllWaypoints');

const AdminToolsPanel: React.FC<AdminToolsPanelProps> = ({
  showToolsIntro,
  handleDismissToolsIntro,
  showSystemSettingsHelp,
  setShowSystemSettingsHelp,
  showExternalSyncHelp,
  setShowExternalSyncHelp,
  adminReevaluateButtonEnabled,
  handleToggleAdminReevaluateButton,
  adminSubscriptionPushEnabled,
  handleToggleAdminSubscriptionPush,
  activeTab,
  externalSyncEnabled,
  handleToggleExternalSync,
  handleRunExternalSync,
  externalSyncStatus,
  externalSyncSources,
  setExternalSyncSources,
  handleSaveExternalSyncSources,
  handleToggleExternalSyncSource,
  externalSyncRegions,
  externalSyncRegionValidation,
  handleAddExternalSyncRegion,
  handleUpdateExternalSyncRegion,
  handleRemoveExternalSyncRegion,
  handleSaveExternalSyncRegions,
  regionSearchQuery,
  setRegionSearchQuery,
  regionSearchLoading,
  regionSearchResults,
  handleApplyRegionSearchResult,
  processingId,
  verifiedFeedUrl,
  handleCopyFeedUrl,
  feedCopied,
  sitemapUrl,
  handleCopySitemapUrl,
  sitemapCopied,
  loadSeoReport,
  seoReportLoading,
  seoReport,
  confirmClearArea,
  setConfirmClearArea,
  executeClearArea,
  isClearingArea,
  executeReevaluation,
  handleAddRegionFromCurrentView,
  nordicCountryConfig,
  handleToggleNordicCountry,
}) => {
  const { t } = useTranslation();
  const [pushTestStatus, setPushTestStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [pushDiagnostics, setPushDiagnostics] = useState<Array<{ email: string; notifEnabled?: boolean; hasToken?: boolean; tokenPrefix?: string | null; pushResult?: string; status?: string }>>([]);
  const [registerStatus, setRegisterStatus] = useState<'idle' | 'registering' | 'ok' | 'failed'>('idle');
  const [registerLog, setRegisterLog] = useState<string[]>([]);
  const [publishAllStatus, setPublishAllStatus] = useState<'idle' | 'dry' | 'running' | 'done' | 'error'>('idle');
  const [publishAllResult, setPublishAllResult] = useState<{ processed?: number; published?: number; skipped?: number; failed?: number; dryRun?: boolean } | null>(null);
  const [dedupeStatus, setDedupeStatus] = useState<'idle' | 'dry' | 'running' | 'done' | 'error'>('idle');
  const [dedupeResult, setDedupeResult] = useState<DedupeResult | null>(null);

  const handleDedupeOwnSpots = async (dryRun: boolean) => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    setDedupeStatus(dryRun ? 'dry' : 'running');
    setDedupeResult(null);
    try {
      const res = await dedupeUserCreatedSpots(uid, { dryRun });
      setDedupeResult(res);
      setDedupeStatus('done');
    } catch (e) {
      console.error('dedupeUserCreatedSpots failed:', e);
      setDedupeStatus('error');
    }
  };

  const handlePublishAllWaypoints = async (dryRun: boolean) => {
    setPublishAllStatus(dryRun ? 'dry' : 'running');
    setPublishAllResult(null);
    try {
      const res = await adminPublishAllWaypoints({ dryRun });
      setPublishAllResult(res.data as any);
      setPublishAllStatus('done');
    } catch (e) {
      console.error('adminPublishAllWaypoints failed:', e);
      setPublishAllStatus('error');
    }
  };

  const handleTestPush = async () => {
    setPushTestStatus('sending');
    setPushDiagnostics([]);
    try {
      const res = await testAdminPush();
      const data = res.data as { ok: boolean; results: typeof pushDiagnostics };
      setPushDiagnostics(data.results || []);
      setPushTestStatus('sent');
      setTimeout(() => setPushTestStatus('idle'), 15000);
    } catch {
      setPushTestStatus('error');
      setTimeout(() => setPushTestStatus('idle'), 5000);
    }
  };

  return (
    <div className="space-y-6 animate-in slide-in-from-bottom-4">
      {showToolsIntro && (
        <div className="bg-blue-500/10 border border-blue-500/20 p-4 rounded-2xl space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-xs font-black uppercase tracking-widest text-blue-300">{t('admin.tools_new_title', 'Nytt i adminverktøy')}</h3>
              <p className="text-2xs text-[var(--text-secondary)] mt-1">{t('admin.tools_new_subtitle', 'Korte forklaringer på de nye funksjonene i denne fanen.')}</p>
            </div>
            <button
              onClick={handleDismissToolsIntro}
              className="px-2.5 py-1 rounded-lg bg-blue-900/30 border border-blue-500/30 text-2xs font-black uppercase tracking-widest text-blue-200 hover:bg-blue-800/30"
            >
              {t('common.dismiss', 'Ikke vis igjen')}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-2xs text-[var(--text-secondary)]">
            <div className="bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl p-2.5"><strong>{t('admin.system_settings', 'Systeminnstillinger')}</strong>: {t('admin.system_settings_desc', 'globale brytere for app-adferd.')}</div>
            <div className="bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl p-2.5"><strong>{t('admin.background_sync', 'Bakgrunnssynk')}</strong>: {t('admin.background_sync_desc', 'importerer NVDB/OSM til cached_spots.')}</div>
            <div className="bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl p-2.5"><strong>{t('admin.sync_regions', 'Synk-regioner')}</strong>: {t('admin.sync_regions_desc', 'områder (bbox) hvor importen kjører.')}</div>
          </div>
        </div>
      )}
      {/* System Settings */}
      <div className="bg-purple-500/10 border border-purple-500/20 p-6 rounded-panel space-y-4">
        <div className="flex items-center justify-between gap-3 text-purple-500">
          <div className="flex items-center gap-3">
            <Activity size={24} />
            <div>
              <h3 className="text-sm font-black uppercase tracking-widest">{t('admin.system_settings', 'Systeminnstillinger')}</h3>
              <p className="text-2xs font-bold opacity-70">{t('admin.system_settings_subtitle', 'Overordnede innstillinger for appen')}</p>
            </div>
          </div>
          <button
            onClick={() => setShowSystemSettingsHelp((v) => !v)}
            className="p-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border-secondary)] text-[var(--text-primary)] hover:bg-[var(--bg-input)]"
            title="Hva gjør disse innstillingene?"
          >
            <HelpCircle size={14} />
          </button>
        </div>
        {showSystemSettingsHelp && (
          <div className="bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl p-3 text-2xs text-[var(--text-secondary)] space-y-1">
            <p>`{t('admin.reevaluate_btn', 'Admin revurder-knapp')}`: {t('admin.reevaluate_btn_help', 'viser/skjuler admin-knappen i kartet.')}</p>
            <p>`XML-feed / Sitemap`: {t('admin.feed_sitemap_help', 'lenker for ekstern publisering og SEO.')}</p>
          </div>
        )}

        <div className="flex items-center justify-between p-4 bg-[var(--bg-surface-alt)] rounded-2xl border border-[var(--border-primary)]">
          <div>
            <h4 className="text-[var(--text-primary)] text-xs font-black uppercase tracking-widest mb-1">{t('admin.reevaluate_btn', 'Admin: Revurder-knapp i kart')}</h4>
            <p className="text-2xs text-[var(--text-secondary)]">{t('admin.reevaluate_btn_desc', 'Vis/skjul den oransje knappen øverst i kartet for admin-brukere.')}</p>
          </div>
          <button
            onClick={() => handleToggleAdminReevaluateButton(!adminReevaluateButtonEnabled)}
            disabled={processingId === 'admin-reevaluate-toggle'}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${adminReevaluateButtonEnabled ? 'bg-amber-600' : 'bg-[var(--bg-input)]'}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${adminReevaluateButtonEnabled ? 'translate-x-6' : 'translate-x-1'}`}
            />
          </button>
        </div>

        <div className="flex items-center justify-between p-4 bg-[var(--bg-surface-alt)] rounded-2xl border border-[var(--border-primary)]">
          <div>
            <h4 className="text-[var(--text-primary)] text-xs font-black uppercase tracking-widest mb-1">{t('admin.subscription_push_toggle', 'Admin push ved nytt abonnement')}</h4>
            <p className="text-2xs text-[var(--text-secondary)]">{t('admin.subscription_push_toggle_desc', 'Når aktivert får admin push-varsel når en bruker tegner nytt abonnement.')}</p>
          </div>
          <button
            onClick={() => handleToggleAdminSubscriptionPush(!adminSubscriptionPushEnabled)}
            disabled={processingId === 'admin-subscription-push-toggle'}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${adminSubscriptionPushEnabled ? 'bg-emerald-600' : 'bg-[var(--bg-input)]'}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${adminSubscriptionPushEnabled ? 'translate-x-6' : 'translate-x-1'}`}
            />
          </button>
        </div>

        <div className="p-4 bg-[var(--bg-surface-alt)] rounded-2xl border border-amber-500/30 space-y-3">
          <div>
            <h4 className="text-[var(--text-primary)] text-xs font-black uppercase tracking-widest mb-1">Publiser alle waypoints som Fricamp-plasser</h4>
            <p className="text-2xs text-[var(--text-secondary)] leading-relaxed">
              Bulk-konverterer ALLE brukeres private waypoints (uten linket spot) til offentlige Fricamp-plasser med automatisk kategori basert på navn/notat. Hopper over <code>star</code>/<code>danger</code>-ikoner. Kjør først som <strong>tørrkjøring</strong> for å se antallet.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePublishAllWaypoints(true)}
              disabled={publishAllStatus === 'dry' || publishAllStatus === 'running'}
              className="px-3 py-2 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center gap-2 bg-[var(--bg-input)] border border-[var(--border-primary)] text-[var(--text-primary)] hover:bg-[var(--bg-surface)] disabled:opacity-60"
            >
              {publishAllStatus === 'dry' ? <Loader2 size={12} className="animate-spin" /> : <Activity size={12} />}
              Tørrkjøring
            </button>
            <button
              onClick={() => {
                if (!window.confirm('Er du sikker? Dette publiserer ALLE brukeres private waypoints offentlig. Kan ikke angres uten manuell sletting.')) return;
                handlePublishAllWaypoints(false);
              }}
              disabled={publishAllStatus === 'dry' || publishAllStatus === 'running'}
              className="px-3 py-2 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-60"
            >
              {publishAllStatus === 'running' ? <Loader2 size={12} className="animate-spin" /> : <ShieldAlert size={12} />}
              Publiser alle
            </button>
          </div>
          {publishAllResult && (
            <div className="text-2xs text-[var(--text-primary)] p-2 rounded-lg bg-[var(--bg-input)] font-mono">
              {publishAllResult.dryRun ? 'Tørrkjøring: ' : 'Kjørt: '}
              behandlet {publishAllResult.processed}, publisert {publishAllResult.published}, hoppet over {publishAllResult.skipped}, feilet {publishAllResult.failed}
            </div>
          )}
          {publishAllStatus === 'error' && (
            <div className="text-2xs text-red-400">Feil — sjekk konsoll-loggen</div>
          )}
        </div>

        <div className="p-4 bg-[var(--bg-surface-alt)] rounded-2xl border border-amber-500/30 space-y-3">
          <div>
            <h4 className="text-[var(--text-primary)] text-xs font-black uppercase tracking-widest mb-1">Dedupe egne spots</h4>
            <p className="text-2xs text-[var(--text-secondary)] leading-relaxed">
              Finner cached_spots opprettet av <strong>din</strong> bruker som er duplikater på samme lokasjon (~11m) + samme navn. Beholder den eldste i hver gruppe og markerer resten som <code>isInvalid</code>. Kjør tørrkjøring først.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleDedupeOwnSpots(true)}
              disabled={dedupeStatus === 'dry' || dedupeStatus === 'running'}
              className="px-3 py-2 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center gap-2 bg-[var(--bg-input)] border border-[var(--border-primary)] text-[var(--text-primary)] hover:bg-[var(--bg-surface)] disabled:opacity-60"
            >
              {dedupeStatus === 'dry' ? <Loader2 size={12} className="animate-spin" /> : <Activity size={12} />}
              Tørrkjøring
            </button>
            <button
              onClick={() => {
                if (!dedupeResult || dedupeResult.totalToInvalidate === 0) {
                  if (!window.confirm('Ingen tørrkjøring kjørt. Er du sikker du vil kjøre direkte?')) return;
                } else {
                  if (!window.confirm(`Markere ${dedupeResult.totalToInvalidate} duplikater som isInvalid? Beholder ${dedupeResult.groups.length} originale.`)) return;
                }
                handleDedupeOwnSpots(false);
              }}
              disabled={dedupeStatus === 'dry' || dedupeStatus === 'running'}
              className="px-3 py-2 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-60"
            >
              {dedupeStatus === 'running' ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              Kjør dedupe
            </button>
          </div>
          {dedupeResult && (
            <div className="text-2xs text-[var(--text-primary)] p-2 rounded-lg bg-[var(--bg-input)] font-mono space-y-1 max-h-60 overflow-y-auto">
              <div>{dedupeResult.applied ? 'Kjørt: ' : 'Tørrkjøring: '}skannet {dedupeResult.totalScanned} egne spots, fant {dedupeResult.groups.length} duplikatgrupper, totalt {dedupeResult.totalToInvalidate} markert som isInvalid</div>
              {dedupeResult.groups.slice(0, 20).map(g => (
                <div key={g.key} className="text-[var(--text-secondary)]">
                  · {g.name || '(uten navn)'} — beholder {g.keepId.slice(0, 18)}…, {g.duplicateIds.length} duplikat
                </div>
              ))}
              {dedupeResult.groups.length > 20 && <div className="text-[var(--text-muted)]">… og {dedupeResult.groups.length - 20} flere grupper</div>}
            </div>
          )}
          {dedupeStatus === 'error' && (
            <div className="text-2xs text-red-400">Feil — sjekk konsoll-loggen</div>
          )}
        </div>

        <div className="flex items-center justify-between p-4 bg-[var(--bg-surface-alt)] rounded-2xl border border-[var(--border-primary)]">
          <div>
            <h4 className="text-[var(--text-primary)] text-xs font-black uppercase tracking-widest mb-1">Test push-varsling</h4>
            <p className="text-2xs text-[var(--text-secondary)]">Sender testvarsel til alle admin-enheter.</p>
          </div>
          <button
            onClick={handleTestPush}
            disabled={pushTestStatus === 'sending'}
            className={`px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center gap-2 transition-all ${
              pushTestStatus === 'sent' ? 'bg-emerald-600 text-white' :
              pushTestStatus === 'error' ? 'bg-red-600 text-white' :
              'bg-blue-600 hover:bg-blue-500 text-white'
            } disabled:opacity-60`}
          >
            {pushTestStatus === 'sending' ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />}
            {pushTestStatus === 'sent' ? t('admin.tools.sent') : pushTestStatus === 'error' ? t('admin.tools.error') : t('admin.tools.test_push')}
          </button>
        </div>

        {pushDiagnostics.length > 0 && (
          <div className="p-3 bg-[var(--bg-surface)] rounded-xl border border-[var(--border-primary)] space-y-2">
            <h5 className="text-2xs font-black uppercase tracking-widest text-[var(--text-secondary)]">Push-diagnostikk</h5>
            {pushDiagnostics.map((d, i) => (
              <div key={i} className="text-2xs text-[var(--text-primary)] space-y-0.5">
                <div className="font-bold">{d.email}</div>
                {d.status === 'no_user_doc' ? (
                  <div className="text-red-400">Ingen brukerdokument funnet</div>
                ) : (
                  <>
                    <div>Varsler aktivert: <span className={d.notifEnabled ? 'text-emerald-400' : 'text-red-400'}>{d.notifEnabled ? t('common.yes') : t('common.no')}</span></div>
                    <div>FCM-token: <span className={d.hasToken ? 'text-emerald-400' : 'text-red-400'}>{d.hasToken ? d.tokenPrefix : t('admin.tools.missing')}</span></div>
                    <div>Resultat: <span className={d.pushResult === 'sent_ok' ? 'text-emerald-400' : 'text-amber-400'}>{d.pushResult}</span></div>
                  </>
                )}
              </div>
            ))}
            <button
              onClick={async () => {
                const uid = auth.currentUser?.uid;
                if (!uid) { setRegisterLog(['Ikke logget inn']); return; }
                setRegisterStatus('registering');
                setRegisterLog([]);
                const log: string[] = [];
                try {
                  // Steg 1: Nettleser-tillatelse
                  if (!('Notification' in window)) { log.push('Notification API mangler'); setRegisterLog(log); setRegisterStatus('failed'); return; }
                  log.push('Ber om tillatelse...');
                  setRegisterLog([...log]);
                  const perm = await Notification.requestPermission();
                  log.push(`Tillatelse: ${perm}`);
                  setRegisterLog([...log]);
                  if (perm !== 'granted') { setRegisterStatus('failed'); return; }

                  // Steg 2: Sett notificationsEnabled
                  await enableNotifications(uid, true);
                  log.push('notificationsEnabled satt til true');
                  setRegisterLog([...log]);

                  // Steg 3: Service worker
                  if (!messaging) { log.push('messaging er null!'); setRegisterLog([...log]); setRegisterStatus('failed'); return; }
                  let swReg = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
                  if (!swReg) {
                    log.push('SW ikke funnet, registrerer...');
                    setRegisterLog([...log]);
                    swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
                    await navigator.serviceWorker.ready;
                  }
                  log.push(`SW: ${swReg.scope}`);
                  setRegisterLog([...log]);

                  // Steg 4: FCM token
                  // @ts-ignore
                  const vapidKey = import.meta.env?.VITE_VAPID_KEY as string;
                  log.push(`VAPID: ${vapidKey?.substring(0, 12)}... (${vapidKey?.length} tegn)`);
                  setRegisterLog([...log]);
                  const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: swReg });
                  if (token) {
                    await updateFcmToken(uid, token);
                    log.push(`Token lagret: ${token.substring(0, 20)}...`);
                    setRegisterStatus('ok');
                  } else {
                    log.push('getToken returnerte null');
                    setRegisterStatus('failed');
                  }
                  setRegisterLog([...log]);
                  setTimeout(() => setRegisterStatus('idle'), 10000);
                } catch (err: unknown) {
                  const msg = err instanceof Error ? err.message : String(err);
                  log.push(`Feil: ${msg}`);
                  setRegisterLog([...log]);
                  setRegisterStatus('failed');
                  setTimeout(() => setRegisterStatus('idle'), 15000);
                }
              }}
              disabled={registerStatus === 'registering'}
              className={`mt-2 w-full px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${
                registerStatus === 'ok' ? 'bg-emerald-600 text-white' :
                registerStatus === 'failed' ? 'bg-red-600 text-white' :
                'bg-violet-600 hover:bg-violet-500 text-white'
              } disabled:opacity-60`}
            >
              {registerStatus === 'registering' ? <Loader2 size={12} className="animate-spin" /> : <Wifi size={12} />}
              {registerStatus === 'ok' ? 'Registrert!' : registerStatus === 'failed' ? 'Feilet — sjekk tillatelser' : 'Registrer denne enheten for push'}
            </button>
            {registerLog.length > 0 && (
              <div className="mt-2 p-2 bg-black/30 rounded-lg space-y-0.5">
                {registerLog.map((line, i) => (
                  <div key={i} className="text-3xs font-mono text-[var(--text-secondary)]">{line}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Nordic Country Expansion */}
        <div className="p-4 bg-[var(--bg-surface-alt)] rounded-2xl border border-[var(--border-primary)] space-y-3">
          <h4 className="text-[var(--text-primary)] text-xs font-black uppercase tracking-widest">
            Nordisk ekspansjon
          </h4>
          <p className="text-2xs text-[var(--text-secondary)]">
            Aktiver land for alle brukere. Admin ser alltid kartet uten overlay.
          </p>
          {([
            { key: 'sweden', label: 'Sverige', flag: '\u{1F1F8}\u{1F1EA}' },
            { key: 'denmark', label: 'Danmark', flag: '\u{1F1E9}\u{1F1F0}' },
            { key: 'finland', label: 'Finland', flag: '\u{1F1EB}\u{1F1EE}' },
            { key: 'iceland', label: 'Island', flag: '\u{1F1EE}\u{1F1F8}' },
          ] as const).map(({ key, label, flag }) => (
            <div key={key} className="flex items-center justify-between py-2">
              <span className="text-[var(--text-primary)] text-xs font-bold">
                {flag} {label}
              </span>
              <button
                onClick={() => handleToggleNordicCountry(key, !nordicCountryConfig[key])}
                disabled={processingId === `nordic-${key}-toggle`}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                  nordicCountryConfig[key] ? 'bg-emerald-600' : 'bg-[var(--bg-input)]'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    nordicCountryConfig[key] ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>

        <div className="p-4 bg-[var(--bg-surface-alt)] rounded-2xl border border-[var(--border-primary)] space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h4 className="text-[var(--text-primary)] text-xs font-black uppercase tracking-widest mb-1">{t('admin.background_sync_title', 'Bakgrunnssynk: NVDB/OSM')}</h4>
              <p className="text-2xs text-[var(--text-secondary)]">{t('admin.background_sync_subtitle', 'Kjører periodisk import til cached_spots for eksisterende kartlag.')}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowExternalSyncHelp((v) => !v)}
                className="p-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border-secondary)] text-[var(--text-primary)] hover:bg-[var(--bg-input)]"
                title="Hva gjør synk og regioner?"
              >
                <HelpCircle size={14} />
              </button>
              <button
                onClick={() => handleToggleExternalSync(!externalSyncEnabled)}
                disabled={processingId === 'external-sync-toggle'}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${externalSyncEnabled ? 'bg-emerald-600' : 'bg-[var(--bg-input)]'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${externalSyncEnabled ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
            </div>
          </div>
          {showExternalSyncHelp && (
            <div className="bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl p-3 text-2xs text-[var(--text-secondary)] space-y-1">
              <p>`Kjør synk nå`: manuell import i tillegg til planlagt kjøring.</p>
              <p>`Kilder`: styrer hvilke datatyper som hentes inn.</p>
              <p>`Synk-regioner`: bbox-områder hvor importen kjøres. Ugyldige regioner blokkeres ved lagring.</p>
            </div>
          )}

          <div className="flex flex-col md:flex-row gap-2">
            <button
              onClick={handleRunExternalSync}
              disabled={processingId === 'external-sync-run'}
              className="px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-60"
            >
              {processingId === 'external-sync-run' ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              {t('admin.run_sync_now', 'Kjør synk nå')}
            </button>
            <div className="flex-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2 text-2xs text-[var(--text-secondary)]">
              {t('admin.sync_status', 'Status')}: {externalSyncStatus?.running ? t('admin.sync_running', 'Kjører') : t('admin.sync_inactive', 'Inaktiv')}{externalSyncStatus?.message ? ` · ${externalSyncStatus.message}` : ''}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="flex items-center justify-between bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2 text-2xs text-[var(--text-secondary)]">
              <span>NVDB</span>
              <button
                onClick={() => handleToggleExternalSyncSource('nvdb')}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${externalSyncSources.nvdb ? 'bg-emerald-600' : 'bg-[var(--bg-input)]'}`}
              >
                <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${externalSyncSources.nvdb ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
            </div>
            <div className="flex items-center justify-between bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2 text-2xs text-[var(--text-secondary)]">
              <span>OSM Amenities</span>
              <button
                onClick={() => handleToggleExternalSyncSource('osmAmenities')}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${externalSyncSources.osmAmenities ? 'bg-emerald-600' : 'bg-[var(--bg-input)]'}`}
              >
                <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${externalSyncSources.osmAmenities ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
            </div>
            <div className="flex items-center justify-between bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2 text-2xs text-[var(--text-secondary)]">
              <span>OSM Camping</span>
              <button
                onClick={() => handleToggleExternalSyncSource('osmCamp')}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${externalSyncSources.osmCamp ? 'bg-emerald-600' : 'bg-[var(--bg-input)]'}`}
              >
                <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${externalSyncSources.osmCamp ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-2xs font-black uppercase tracking-widest text-[var(--text-secondary)]">{t('admin.sync_regions_label', 'Synk-regioner (bbox)')}</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleAddRegionFromCurrentView}
                  className="px-3 py-1.5 rounded-lg bg-blue-900/30 hover:bg-blue-800/30 border border-blue-500/30 text-2xs font-black uppercase tracking-widest text-blue-200"
                >
                  {t('admin.use_map_view', 'Bruk kartutsnitt')}
                </button>
                <button
                  onClick={handleAddExternalSyncRegion}
                  className="px-3 py-1.5 rounded-lg bg-[var(--bg-input)] hover:bg-[var(--bg-input)] text-2xs font-black uppercase tracking-widest text-[var(--text-primary)] flex items-center gap-1.5"
                >
                  <Edit3 size={12} /> {t('admin.add_manually', 'Legg til manuelt')}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <input
                value={regionSearchQuery}
                onChange={(e) => setRegionSearchQuery(e.target.value)}
                placeholder={t('admin.region_search_placeholder', 'Søk sted/kommune/fylke i Norge...')}
                className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg px-3 py-2 text-2xs text-[var(--text-primary)]"
              />
              {regionSearchLoading && (
                <div className="text-2xs text-[var(--text-secondary)] flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" /> {t('admin.searching_regions', 'Søker regioner...')}
                </div>
              )}
              {!regionSearchLoading && regionSearchResults.length > 0 && (
                <div className="max-h-44 overflow-auto rounded-xl border border-[var(--border-secondary)] bg-[var(--bg-input)] divide-y divide-[var(--border-primary)]">
                  {regionSearchResults.map((r, i) => (
                    <button
                      key={`${r.display_name}-${i}`}
                      onClick={() => handleApplyRegionSearchResult(r)}
                      className="w-full text-left px-3 py-2 hover:bg-[var(--bg-surface-alt)] transition-colors"
                    >
                      <p className="text-2xs text-[var(--text-primary)] font-bold line-clamp-1">{r.display_name}</p>
                      <p className="text-3xs text-[var(--text-muted)]">Klikk for å legge til region med automatisk bbox</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {externalSyncRegions.length === 0 ? (
              <div className="bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2 text-2xs text-[var(--text-muted)]">
                {t('admin.no_regions', 'Ingen regioner konfigurert.')}
              </div>
            ) : (
              <div className="space-y-2">
                {externalSyncRegions.map((region, idx) => (
                  <div key={`${region.id}-${idx}`} className={`bg-[var(--bg-input)] border rounded-xl p-3 space-y-2 ${externalSyncRegionValidation.invalidIndexes.has(idx) ? 'border-red-500/40' : 'border-[var(--border-secondary)]'}`}>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <input
                        value={region.id}
                        onChange={(e) => handleUpdateExternalSyncRegion(idx, 'id', e.target.value)}
                        className="bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg px-2 py-1.5 text-2xs text-[var(--text-primary)]"
                        placeholder="region-id"
                      />
                      <input
                        value={region.name}
                        onChange={(e) => handleUpdateExternalSyncRegion(idx, 'name', e.target.value)}
                        className="md:col-span-2 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg px-2 py-1.5 text-2xs text-[var(--text-primary)]"
                        placeholder={t('admin.region_name', 'Regionnavn')}
                      />
                      <div className="flex items-center justify-between bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg px-2 py-1.5 text-2xs text-[var(--text-secondary)]">
                        <span>{t('common.active', 'Aktiv')}</span>
                        <button
                          onClick={() => handleUpdateExternalSyncRegion(idx, 'enabled', !region.enabled)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${region.enabled ? 'bg-emerald-600' : 'bg-[var(--bg-input)]'}`}
                        >
                          <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${region.enabled ? 'translate-x-5' : 'translate-x-1'}`} />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      <input value={region.bounds.latMin} onChange={(e) => handleUpdateExternalSyncRegion(idx, 'latMin', e.target.value)} className="bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg px-2 py-1.5 text-2xs text-[var(--text-primary)]" placeholder="latMin" />
                      <input value={region.bounds.lonMin} onChange={(e) => handleUpdateExternalSyncRegion(idx, 'lonMin', e.target.value)} className="bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg px-2 py-1.5 text-2xs text-[var(--text-primary)]" placeholder="lonMin" />
                      <input value={region.bounds.latMax} onChange={(e) => handleUpdateExternalSyncRegion(idx, 'latMax', e.target.value)} className="bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg px-2 py-1.5 text-2xs text-[var(--text-primary)]" placeholder="latMax" />
                      <input value={region.bounds.lonMax} onChange={(e) => handleUpdateExternalSyncRegion(idx, 'lonMax', e.target.value)} className="bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg px-2 py-1.5 text-2xs text-[var(--text-primary)]" placeholder="lonMax" />
                      <button
                        onClick={() => handleRemoveExternalSyncRegion(idx)}
                        className="px-2 py-1.5 rounded-lg bg-red-900/30 hover:bg-red-800/40 border border-red-500/30 text-2xs font-black uppercase tracking-widest text-red-300"
                      >
                        {t('common.remove', 'Fjern')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!externalSyncRegionValidation.isValid && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 space-y-1">
                <p className="text-2xs font-black uppercase tracking-widest text-red-300">{t('admin.validation_error', 'Valideringsfeil')}</p>
                {externalSyncRegionValidation.issues.slice(0, 5).map((issue, i) => (
                  <p key={i} className="text-2xs text-red-200">{issue}</p>
                ))}
              </div>
            )}

            <button
              onClick={handleSaveExternalSyncRegions}
              disabled={processingId === 'external-sync-save-regions' || !externalSyncRegionValidation.isValid}
              className="w-full px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-60"
            >
              {processingId === 'external-sync-save-regions' ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              {t('admin.save_sync_regions', 'Lagre regioner')}
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="bg-[var(--bg-input)] rounded-xl border border-[var(--border-secondary)] p-2.5">
              <p className="text-4xs text-[var(--text-muted)] uppercase font-black">{t('admin.regions_label', 'Regioner')}</p>
              <p className="text-xs font-black text-[var(--text-primary)]">{externalSyncStatus?.counters?.regions ?? '-'}</p>
            </div>
            <div className="bg-[var(--bg-input)] rounded-xl border border-[var(--border-secondary)] p-2.5">
              <p className="text-4xs text-[var(--text-muted)] uppercase font-black">Hentet</p>
              <p className="text-xs font-black text-[var(--text-primary)]">{externalSyncStatus?.counters?.totalFetched ?? '-'}</p>
            </div>
            <div className="bg-[var(--bg-input)] rounded-xl border border-[var(--border-secondary)] p-2.5">
              <p className="text-4xs text-[var(--text-muted)] uppercase font-black">Skrevet</p>
              <p className="text-xs font-black text-emerald-400">{externalSyncStatus?.counters?.totalWritten ?? '-'}</p>
            </div>
            <div className="bg-[var(--bg-input)] rounded-xl border border-[var(--border-secondary)] p-2.5">
              <p className="text-4xs text-[var(--text-muted)] uppercase font-black">Feil</p>
              <p className="text-xs font-black text-amber-400">{externalSyncStatus?.counters?.errors ?? '-'}</p>
            </div>
          </div>
        </div>

        <div className="p-4 bg-[var(--bg-surface-alt)] rounded-2xl border border-[var(--border-primary)] space-y-3">
          <div>
            <h4 className="text-[var(--text-primary)] text-xs font-black uppercase tracking-widest mb-1">{t('admin.xml_feed_title', 'XML-feed: siste verifiserte')}</h4>
            <p className="text-2xs text-[var(--text-secondary)]">{t('admin.xml_feed_desc', 'Bruk denne lenken på hjemmesiden for å hente de 3 siste verifiserte lokasjonene.')}</p>
          </div>
          <div className="flex flex-col md:flex-row gap-2">
            <input
              value={verifiedFeedUrl}
              readOnly
              className="flex-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2 text-2xs text-[var(--text-secondary)] font-mono"
            />
            <button
              onClick={handleCopyFeedUrl}
              className={`px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${feedCopied ? 'bg-emerald-600 text-white' : 'bg-purple-600 hover:bg-purple-500 text-white'}`}
            >
              <Copy size={12} /> {feedCopied ? t('common.copied', 'Kopiert') : t('common.copy_link', 'Kopier lenke')}
            </button>
          </div>
        </div>

        <div className="p-4 bg-[var(--bg-surface-alt)] rounded-2xl border border-[var(--border-primary)] space-y-3">
          <div>
            <h4 className="text-[var(--text-primary)] text-xs font-black uppercase tracking-widest mb-1">SEO Sitemap</h4>
            <p className="text-2xs text-[var(--text-secondary)]">Send denne til Google Search Console for raskere indeksering av fylke/kommune-sider.</p>
          </div>
          <div className="flex flex-col md:flex-row gap-2">
            <input
              value={sitemapUrl}
              readOnly
              className="flex-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2 text-2xs text-[var(--text-secondary)] font-mono"
            />
            <button
              onClick={handleCopySitemapUrl}
              className={`px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${sitemapCopied ? 'bg-emerald-600 text-white' : 'bg-indigo-600 hover:bg-indigo-500 text-white'}`}
            >
              <Copy size={12} /> {sitemapCopied ? t('common.copied', 'Kopiert') : t('common.copy_link', 'Kopier lenke')}
            </button>
          </div>
        </div>

        <div className="p-4 bg-[var(--bg-surface-alt)] rounded-2xl border border-[var(--border-primary)] space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className="text-[var(--text-primary)] text-xs font-black uppercase tracking-widest mb-1">SEO-rapport (måned)</h4>
              <p className="text-2xs text-[var(--text-secondary)]">Visninger av programmatic SEO-sider (mennesker/boter) siste måned.</p>
            </div>
            <button
              onClick={loadSeoReport}
              disabled={seoReportLoading}
              className="px-3 py-1.5 rounded-lg bg-[var(--bg-input)] hover:bg-[var(--bg-input)] text-2xs font-black uppercase tracking-widest text-[var(--text-primary)] flex items-center gap-1.5"
            >
              {seoReportLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Oppdater
            </button>
          </div>

          {!seoReport ? (
            <p className="text-2xs text-[var(--text-muted)]">Ingen rapportdata tilgjengelig ennå.</p>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="bg-[var(--bg-input)] rounded-xl border border-[var(--border-secondary)] p-3">
                  <p className="text-3xs text-[var(--text-muted)] uppercase font-black">Måned</p>
                  <p className="text-sm font-black text-[var(--text-primary)]">{seoReport.month}</p>
                </div>
                <div className="bg-[var(--bg-input)] rounded-xl border border-[var(--border-secondary)] p-3">
                  <p className="text-3xs text-[var(--text-muted)] uppercase font-black">Totale visninger</p>
                  <p className="text-sm font-black text-[var(--text-primary)]">{seoReport.totals.views}</p>
                </div>
                <div className="bg-[var(--bg-input)] rounded-xl border border-[var(--border-secondary)] p-3">
                  <p className="text-3xs text-[var(--text-muted)] uppercase font-black">Mennesker</p>
                  <p className="text-sm font-black text-emerald-400">{seoReport.totals.humanViews}</p>
                </div>
                <div className="bg-[var(--bg-input)] rounded-xl border border-[var(--border-secondary)] p-3">
                  <p className="text-3xs text-[var(--text-muted)] uppercase font-black">Boter</p>
                  <p className="text-sm font-black text-amber-400">{seoReport.totals.botViews}</p>
                </div>
              </div>

              <div className="max-h-56 overflow-auto rounded-xl border border-[var(--border-secondary)]">
                <table className="w-full text-2xs">
                  <thead className="bg-[var(--bg-input)] text-[var(--text-secondary)] uppercase tracking-widest">
                    <tr>
                      <th className="text-left px-3 py-2">Side</th>
                      <th className="text-right px-3 py-2">Visninger</th>
                      <th className="text-right px-3 py-2">Mennesker</th>
                      <th className="text-right px-3 py-2">Boter</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seoReport.topPages.map((row) => (
                      <tr key={row.page} className="border-t border-[var(--border-primary)]">
                        <td className="px-3 py-2 text-[var(--text-primary)] font-mono">/fricamping/{row.page}</td>
                        <td className="px-3 py-2 text-right text-[var(--text-primary)] font-black">{row.views}</td>
                        <td className="px-3 py-2 text-right text-emerald-400 font-black">{row.humanViews}</td>
                        <td className="px-3 py-2 text-right text-amber-400 font-black">{row.botViews}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mass Delete Area */}
      <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-panel space-y-4">
        <div className="flex items-center gap-3 text-red-500">
          <Eraser size={24} />
          <div>
            <h3 className="text-sm font-black uppercase tracking-widest">{t('admin.clear_map', 'Rydde Kart')}</h3>
            <p className="text-2xs font-bold opacity-70">{t('admin.clear_map_subtitle', 'Masse-sletting av AI-generert innhold')}</p>
          </div>
        </div>

        <p className="text-[var(--text-secondary)] text-xs leading-relaxed">
          Dette verktøyet sletter alle punkter merket som <strong>"Algorithmic"</strong> (AI-genererte fricamper, snuplasser, blindveier) som er synlige i det <strong className="text-[var(--text-primary)]">nåværende kartutsnittet bak dette vinduet</strong>.
          <br /><br />
          Punkt som er manuelt lagt til, favoritter, eller verifiserte rasteplasser (NVDB) vil IKKE bli berørt.
        </p>

        {!confirmClearArea ? (
          <button
            onClick={() => setConfirmClearArea(true)}
            className="w-full bg-red-600 hover:bg-red-500 text-white py-4 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95"
          >
            <Trash2 size={16} /> {t('admin.clear_ai_spots_btn', 'Slett AI-punkter i dette utsnittet')}
          </button>
        ) : (
          <div className="space-y-3 p-4 bg-red-500/10 rounded-xl border border-red-500/30 animate-in zoom-in-95">
            <p className="text-2xs font-black text-red-400 uppercase text-center">Er du helt sikker? Dette kan ikke angres.</p>
            <div className="flex gap-3">
              <button
                onClick={executeClearArea}
                disabled={isClearingArea}
                className="flex-1 bg-red-600 text-white py-3 rounded-xl text-2xs font-black uppercase flex items-center justify-center gap-2 hover:bg-red-500 active:scale-95 transition-all"
              >
                {isClearingArea ? <Loader2 size={14} className="animate-spin" /> : <ShieldAlert size={14} />}
                JA, SLETT ALT
              </button>
              <button
                onClick={() => setConfirmClearArea(false)}
                className="flex-1 bg-[var(--bg-input)] text-[var(--text-secondary)] py-3 rounded-xl text-2xs font-black uppercase hover:bg-[var(--bg-input)] active:scale-95 transition-all"
              >
                {t('common.cancel', 'Avbryt')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Re-evaluation Tool */}
      <div className="bg-amber-500/10 border border-amber-500/20 p-6 rounded-panel space-y-4">
        <div className="flex items-center gap-3 text-amber-500">
          <Microscope size={24} />
          <div>
            <h3 className="text-sm font-black uppercase tracking-widest">{t('admin.reevaluate_spots', 'Revurder synlige punkter')}</h3>
            <p className="text-2xs font-bold opacity-70">{t('admin.reevaluate_spots_subtitle', 'Dyp-analyse og kvalitetsvask')}</p>
          </div>
        </div>

        <p className="text-[var(--text-secondary)] text-xs leading-relaxed">
          Dette kjører en <strong>3-trinns sjekk</strong> på alle AI-punkter i det synlige kartet:
          <br /><br />
          1. <strong>Hus-sjekk:</strong> Er punktet &lt;150m fra hus? (Slettes)
          <br />
          2. <strong>Vei-sjekk:</strong> Er det mer enn 100m til vei? (Slettes hvis ikke sti/fasilitet)
          <br />
          3. <strong>AI-Analyse:</strong> Ny Gemini-vurdering. Hvis score &lt; 4 eller "FORBUDT", slettes punktet.
          <br /><br />
          <em className="text-amber-200">NB: Koster API-tokens per punkt. Bruk med omhu.</em>
        </p>

        <button
          onClick={executeReevaluation}
          className="w-full bg-amber-600 hover:bg-amber-500 text-white py-4 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95"
        >
          <RefreshCw size={16} /> {t('admin.start_reevaluation_btn', 'Start revurdering av utsnitt')}
        </button>
      </div>
    </div>
  );
};

export default AdminToolsPanel;
